import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { generateNanoId } from '../../../../utils/generateNanoId';
import { useCurrentUser } from '../../../../auth/hooks/useAuth';
import { useCreateLifestyleGeneration } from '../../../lifestyleGenerations/hooks/useCreateLifestyleGeneration';
import { LIFESTYLE_GENERATION_POLLING_INTERVAL } from '../../../lifestyleGenerations/lifestyleGenerationsConstants';
import { LifestyleGenerationsBffUrl } from '../../../lifestyleGenerations/LifestyleGenerationsBffUrl';
import {
  CreateLifestyleGenerationInput,
  FetchLifestyleGenerationResponse,
  LifestyleGenerationStatus,
  LifestyleGenerationType
} from '../../../lifestyleGenerations/lifestyleGenerationsTypes';
import {
  GenerationPollResult,
  useGenerationPolling
} from '../../../modelViewers/components/content/ModelViewerEditor/hooks/useModelViewerLifestyleGeneration/useGenerationPolling';
import { useReferenceFilesUpload } from '../../../modelViewers/components/content/ModelViewerEditor/hooks/useModelViewerLifestyleGeneration/useReferenceFilesUpload';

export interface ComposerLifestyleGenerationItem {
  kind: 'lifestyle';
  createdOrder: number;
  localId: string;
  serverId?: string;
  status: LifestyleGenerationStatus;
  prompt: string;
  referenceFileUrls?: string[];
  resultPreviewUrl?: string;
  resultFullUrl?: string;
  errorMessage?: string;
}

interface UseComposerLifestyleGenerationOptions {
  // Shared with useComposerRenderGeneration — renders and lifestyles live in
  // the same sidebar/overlay view switcher.
  activeViewId: string;
  setActiveViewId: Dispatch<SetStateAction<string>>;
  // Result url of the render currently shown in the overlay. Null while the
  // active view is not a finished render — when the active view is a finished
  // lifestyle, its own result becomes the generation subject instead.
  subjectImageUrl: string | null;
}

const GEMINI_PROVIDER = 'gemini';
const GEMINI_PROVIDER_MODEL = 'gemini-3.1-flash-image-preview';

function mapLifestyleGenerationPollResponse(
  data: FetchLifestyleGenerationResponse
): GenerationPollResult<LifestyleGenerationStatus> {
  if (data.status === LifestyleGenerationStatus.Success) {
    return {
      status: LifestyleGenerationStatus.Success,
      resultPreviewUrl: data.results[0]?.image_result_preview,
      resultFullUrl: data.results[0]?.image_result,
      isSuccess: true
    };
  }

  if (data.status === LifestyleGenerationStatus.Failed) {
    return {
      status: LifestyleGenerationStatus.Failed,
      errorMessage: data.error_message,
      isFailed: true
    };
  }

  return { status: data.status };
}

// Generates a lifestyle image from a finished composite-render result. Unlike
// useModelViewerLifestyleGeneration there is no screenshot step — the render's
// image url is sent as subject_image_url directly.
function useComposerLifestyleGeneration({
  activeViewId,
  setActiveViewId,
  subjectImageUrl
}: UseComposerLifestyleGenerationOptions) {
  const { createLifestyleGeneration } = useCreateLifestyleGeneration();
  const currentUser = useCurrentUser();

  const [lifestyleGenerations, setLifestyleGenerations] = useState<
    ComposerLifestyleGenerationItem[]
  >([]);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const {
    referenceFiles,
    referenceFileUrls,
    isUploadingReference,
    applyReferenceFiles,
    handleReferenceFilesChange,
    handleRemoveReference
  } = useReferenceFilesUpload();

  const activeLifestyleGeneration = useMemo(
    () => lifestyleGenerations.find((g) => g.localId === activeViewId) ?? null,
    [lifestyleGenerations, activeViewId]
  );

  // What the next generation runs on: the finished render shown in the
  // overlay, or — when the overlay shows a finished lifestyle — that
  // lifestyle's own result (chained regeneration).
  const effectiveSubjectImageUrl =
    subjectImageUrl ?? activeLifestyleGeneration?.resultFullUrl ?? null;

  // Switching the overlay to a lifestyle prefills the prompt input with the
  // prompt that produced it. Ref-guarded to run once per view switch — later
  // poll-driven lifestyleGenerations updates must not clobber user edits.
  const prefilledViewIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (prefilledViewIdRef.current === activeViewId) return;
    prefilledViewIdRef.current = activeViewId;
    const item = lifestyleGenerations.find((g) => g.localId === activeViewId);
    if (item) setPrompt(item.prompt);
  }, [activeViewId, lifestyleGenerations]);

  const handlePollTick = useCallback(
    (
      localId: string,
      result: GenerationPollResult<LifestyleGenerationStatus>
    ) => {
      setLifestyleGenerations((prev) =>
        prev.map((g) => {
          if (g.localId !== localId) return g;
          if (
            g.status === result.status &&
            result.status !== LifestyleGenerationStatus.Success &&
            result.status !== LifestyleGenerationStatus.Failed
          ) {
            return g;
          }
          return {
            ...g,
            status: result.status,
            resultPreviewUrl: result.resultPreviewUrl,
            resultFullUrl: result.resultFullUrl,
            errorMessage: result.errorMessage
          };
        })
      );
    },
    []
  );

  const { startPolling, stopPolling } = useGenerationPolling({
    buildShowUrl: LifestyleGenerationsBffUrl.show,
    mapResponse: mapLifestyleGenerationPollResponse,
    intervalMs: LIFESTYLE_GENERATION_POLLING_INTERVAL,
    onTick: handlePollTick,
    onSuccess: setActiveViewId
  });

  const handleComposerGenerate = useCallback(async () => {
    if (isGenerating || isUploadingReference || !effectiveSubjectImageUrl)
      return;

    setIsGenerating(true);
    setGenerateError(null);

    const localId = generateNanoId();
    setLifestyleGenerations((prev) => [
      {
        kind: 'lifestyle',
        createdOrder: Date.now(),
        localId,
        status: LifestyleGenerationStatus.Pending,
        prompt,
        referenceFileUrls:
          referenceFileUrls.length > 0 ? referenceFileUrls : undefined
      },
      ...prev
    ]);
    setActiveViewId(localId);

    try {
      const input: CreateLifestyleGenerationInput = {
        generation_type: LifestyleGenerationType.Composer,
        subject_image_url: effectiveSubjectImageUrl,
        user_id: currentUser?.id ?? null,
        prompt: prompt || null,
        ...(referenceFileUrls.length > 0 && {
          lifestyle_image_url: referenceFileUrls
        }),
        preferred_provider: GEMINI_PROVIDER,
        preferred_model: GEMINI_PROVIDER_MODEL
      };

      const response = await createLifestyleGeneration(input);

      setLifestyleGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId ? { ...g, serverId: response.id } : g
        )
      );
      startPolling(localId, response.id);
    } catch (err) {
      const message =
        (err as any)?.fullMessages?.[0] ||
        (err as any)?.message ||
        'Generation failed';
      setGenerateError(message);
      setLifestyleGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId
            ? {
                ...g,
                status: LifestyleGenerationStatus.Failed,
                errorMessage: message
              }
            : g
        )
      );
      console.error('[composer lifestyle generation]', err);
    } finally {
      setIsGenerating(false);
    }
  }, [
    isGenerating,
    isUploadingReference,
    effectiveSubjectImageUrl,
    prompt,
    referenceFileUrls,
    currentUser?.id,
    createLifestyleGeneration,
    startPolling,
    setActiveViewId
  ]);

  const dismissLifestyleGeneration = useCallback(
    (localId: string) => {
      stopPolling(localId);
      setLifestyleGenerations((prev) =>
        prev.filter((g) => g.localId !== localId)
      );
      setActiveViewId((prev) => (prev === localId ? 'scene' : prev));
    },
    [stopPolling, setActiveViewId]
  );

  const handleReferenceFileDrop = useCallback(
    (file: File) => applyReferenceFiles([file]),
    [applyReferenceFiles]
  );

  return {
    lifestyleGenerations,
    prompt,
    setPrompt,
    referenceFiles,
    isUploadingReference,
    isGenerating,
    generateError,
    handleReferenceFilesChange,
    handleReferenceFileDrop,
    handleRemoveReference,
    handleComposerGenerate,
    handleRemoveLifestyleGeneration: dismissLifestyleGeneration,
    handleStopLifestyleGeneration: dismissLifestyleGeneration
  };
}

export default useComposerLifestyleGeneration;
